import type { GraphQLError } from 'graphql';

import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';

import AccessRequestSettingsQuery, {
	type useAccessRequestSettingsQuery$data,
	type useAccessRequestSettingsQuery$variables,
} from './admin-controls/hooks/__generated__/useAccessRequestSettingsQuery.graphql.ts';

/**
 * Preloads the query behind `useAccessRequestSettings`, so the restricted-space screen renders the
 * right variant during SSR instead of swapping after hydration.
 *
 * Runs on every route with a space key, View Page included. The gate and the anonymous check are
 * what keep that from costing anything; the anonymous skip is mirrored in RestrictedSpaceScreen and
 * the two should stay in step. `UNSAFE_expValEqualsNoExposure` because exposures must come from the
 * components that render the feature, not from a preloader on every page load.
 */
export async function preloadAccessRequestSettings(): Promise<
	| {
			data: useAccessRequestSettingsQuery$data | null;
			errors?: ReadonlyArray<GraphQLError>;
			hasErrors: boolean;
	  }
	| undefined
> {
	const { cloudId, isLoggedIn } = getPreloaderFnContext();

	if (
		!isLoggedIn ||
		!UNSAFE_expValEqualsNoExposure('cc_perms_ga_access_requests', 'isEnabled', true)
	) {
		return;
	}

	return query<useAccessRequestSettingsQuery$data, useAccessRequestSettingsQuery$variables>({
		query: AccessRequestSettingsQuery,
		variables: { cloudId },
	});
}
